import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { IRootState } from "../../../../../redux/store";
import styles from "./AvatarSelection.module.scss";
import { changeFormData } from "../../../../../redux/slices/formSlice";
import { IAvatarGender, VoiceTypes } from "../../../../../utils/types";
import errorSceneView from "../../../../../assets/pictures/errorSceneView.svg";
import { setAvatar } from "../../../../../redux/slices/avatarSlice";
import { useMediaPreview } from "../../../../PreviewAvatarMedia/MediaPreviewProvider";
import { filterVoicesByGender } from "../../../../../utils/sharedVoices";
import { useTranslation } from "react-i18next";
import { avatarImg } from "../../../../../utils/avatarImages";
import { v4 as uuidv4 } from 'uuid';
import { AvatarOption } from "../../../../AvatarOption/AvatarOption";

export const AvatarSelection: React.FC = () => {
	const { t } = useTranslation('form');
	const formData = useSelector((state: IRootState) => state.avatarForm);
	const voices = useSelector((state: IRootState) => state.voicesList.allVoices);
	const scene = formData.scene.split("/")[1];
	const { setVideoState } = useMediaPreview();
	const avatarId = uuidv4();
	const avatarsRef = useRef<HTMLDivElement>(null);
	const [showOverlay, setShowOverlay] = useState(false);
	const [isScrolling, setIsScrolling] = useState(false);
	
	const dispatch = useDispatch();
	const avatarsList: IAvatarGender[] = useSelector(
		(state: IRootState) => state.platformAvatars,
	);

	useEffect(() => {
		const el = avatarsRef.current;
		if (!el) return;

		const handleScroll = () => {
			setIsScrolling(true);

			const isAtBottom = el.scrollHeight - el.scrollTop === el.clientHeight;
			setShowOverlay(!isAtBottom);

			clearTimeout((handleScroll as any).timeout);
			(handleScroll as any).timeout = setTimeout(() => {
				setIsScrolling(false);
			}, 150);
		};

		el.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => el.removeEventListener("scroll", handleScroll);
	}, []);
	
	const [selectedAvatar, setSelectedAvatar] = useState<string | null>(avatarsList.length > 0 ? formData.type : null);

	const [loadedImages, setLoadedImages] = useState<Record<string, "loading" | "loaded" | "error">>(
        () => avatarsList.reduce((acc, avatar) => ({ ...acc, [avatar.description]: "loading" }), {})
    );

	const handleSelect = useCallback((avatar: IAvatarGender) => {
		setSelectedAvatar(avatar.value);

  		const genderVoices: VoiceTypes[] = filterVoicesByGender(voices, avatar.gender);
		const genderPrompt = avatar.gender === "male" ? "male_prompt" : "female_prompt";

		const defaultText = t(
    		avatar.promptKey || genderPrompt,
    		{ name: t(avatar.description) }
  		);

		dispatch(
    		changeFormData({
      			type: avatar.value,
      			prompt: defaultText + "",
      			voice: genderVoices[0].voice_id,
      			ttsEngine: genderVoices[0].ttsEngine,
    		})
  		);

  		dispatch(
    		setAvatar({
      			gender: avatar.gender,
      			name: avatar.avatarRuName,
      			brainId: "",
      			avatarId: avatarId,
      			promptKey: avatar.promptKey || genderPrompt,
      			enName: avatar.description,
				type: avatar.type || "3d"
    		})
  		);

  		setVideoState("playing");
	}, []);

	const handleImageLoad = useCallback((value: string) => {
        setLoadedImages((prev) => ({ ...prev, [value]: "loaded" }));
    }, []);

	const handleImageError = useCallback((value: string) => {
        setLoadedImages((prev) => ({ ...prev, [value]: "error" }));
    }, []);

	return (
		<div className={styles.avatarsContainer}>
			<h5>{t("choose_appearance")}</h5>
			{avatarsList.length > 0 && (
				<div className={styles.avatarsWrapper}>
					<div className={styles.avatars} ref={avatarsRef}>
						{avatarsList.map((el) => {
							const isSelected = selectedAvatar === el.value;
							const imageStatus = loadedImages[el.value];
							const imageKey = `${el.avatarEnName?.toLowerCase()}_${scene.toLowerCase()}`;
    						const imageSrc = imageStatus === "error" ? errorSceneView : avatarImg[imageKey];

							return (
								<AvatarOption
									key={el.value}
									el={el}
									isSelected={isSelected}
									imageStatus={imageStatus}
									imageKey={imageKey}
									imageSrc={imageSrc}
									type={el.type}
									handleSelect={handleSelect}
									handleImageLoad={handleImageLoad}
									handleImageError={handleImageError}
								/>
							);
						})}
					</div>
					<div className={`${styles.overlayFade} ${
						showOverlay && !isScrolling ? "" : styles.overlayHidden
					}`} />
				</div>
			)}
		</div>
	);
};
